import {Dialog as LumaAccessibleDialog, DialogPanel as LumaAccessiblePanel, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
// ExpiredSessionModal.tsx — Co Marca — Sesión Expirada
import React from 'react';
import { createPortal } from 'react-dom';
import { LogOut, Clock } from 'lucide-react';

interface ExpiredSessionModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const colors = {
    primary:        'var(--ad-primary, #c1348b)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #61257b)',
    secondaryLight: 'var(--ad-secondary, #8b3fa8)',
    accentDark:     '#ea8406',
};

const css = `

.esm-modal *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes esmFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes esmSlideUp{from{opacity:0;transform:translateY(20px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes esmPulse{0%,100%{box-shadow:0 0 0 0 rgba(var(--ad-primary-rgb, 193, 52, 139),.35);}50%{box-shadow:0 0 0 8px rgba(var(--ad-primary-rgb, 193, 52, 139),0);}}
.esm-overlay{animation:esmFadeIn .2s ease both;}
.esm-dialog{animation:esmSlideUp .3s cubic-bezier(.16,1,.3,1) both;}
.esm-icon-pulse{animation:esmPulse 2s infinite;}
.esm-btn{display:inline-flex;align-items:center;gap:8px;background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});color:#fff;border:none;border-radius:12px;padding:12px 28px;font-size:.92rem;font-weight:800;cursor:pointer;box-shadow:0 4px 16px rgba(var(--ad-primary-rgb, 193, 52, 139),.32);transition:all .22s cubic-bezier(.16,1,.3,1);font-family:'DM Sans',sans-serif;}
.esm-btn:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(var(--ad-primary-rgb, 193, 52, 139),.38);}
`;

const ExpiredSessionModal: React.FC<ExpiredSessionModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="esm-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="esm-overlay" style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.65)', backdropFilter:'blur(8px)' }}/>

                <LumaAccessiblePanel data-luma-dialog="true" className="esm-dialog card custom-card vx-card" style={{ position:'relative', background:'var(--ad-surface, #fff)', borderRadius:'28px', width:'100%', maxWidth:'400px', boxShadow:'0 32px 80px rgba(0,0,0,.22)', border:'1.5px solid var(--ad-line, #f1f5f9)', overflow:'hidden', textAlign:'center' }}>

                    {/* Barra top */}
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.primary},${colors.primaryDark},color-mix(in srgb, ${colors.primary} 50.2%, transparent))` }}/>

                    <div style={{ padding:'36px 32px 32px' }}>
                        {/* Icono pulsante */}
                        <div style={{ width:'64px', height:'64px', borderRadius:'20px', background:`color-mix(in srgb, ${colors.primary} 7.06%, transparent)`, border:`1.5px solid color-mix(in srgb, ${colors.primary} 13.33%, transparent)`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }} className="esm-icon-pulse">
                            <Clock size={28} color={colors.primary}/>
                        </div>

                        <LumaAccessibleTitle style={{ fontSize:'1.3rem', fontWeight:'650', color:'var(--ad-ink, #0f172a)', letterSpacing:'-.02em', marginBottom:'10px' }}>
                            Sesión Expirada
                        </LumaAccessibleTitle>

                        {/* Barra de colores */}
                        <div style={{ display:'flex', gap:'4px', justifyContent:'center', marginBottom:'16px' }}>
                            {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                <div key={i} style={{ height:'2px', width:i===0?'20px':'7px', borderRadius:'2px', background:c, opacity:.5 }}/>
                            ))}
                        </div>

                        <p style={{ fontSize:'.92rem', color:'var(--ad-muted, #64748b)', lineHeight:1.7, marginBottom:'28px' }}>
                            Tu sesión ha caducado por inactividad.<br/>
                            <span style={{ fontWeight:'700', color:'var(--ad-ink, #374151)' }}>Inicia sesión nuevamente</span> para continuar.
                        </p>

                        <button onClick={onClose} className="esm-btn">
                            <LogOut size={16}/> Aceptar
                        </button>
                    </div>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        </>,
        document.body
    );
};

export default ExpiredSessionModal;
